import { createFileRoute } from "@tanstack/react-router";
import { Container } from "@/components/site/Container";
import { ButtonLink } from "@/components/site/Button";
import { canonical } from "@/lib/seo";
import { Phone, ShieldCheck } from "lucide-react";

const TITLE = "Roofing Warranty | Durafoam Roofing Phoenix";
const DESCRIPTION =
  "Durafoam Roofing provides workmanship warranties on all roofing work in Phoenix. Learn what is covered and how our warranty process works.";

export const Route = createFileRoute("/warranty")({
  head: () => {
    const url = canonical("/warranty");
    return {
      meta: [
        { title: TITLE },
        { name: "description", content: DESCRIPTION },
        { property: "og:title", content: TITLE },
        { property: "og:description", content: DESCRIPTION },
        { property: "og:url", content: url },
        { property: "og:type", content: "website" },
        { name: "twitter:title", content: TITLE },
        { name: "twitter:description", content: DESCRIPTION },
      ],
      links: [{ rel: "canonical", href: url }],
    };
  },
  component: WarrantyPage,
});

const sections: Array<{ title: string; body: string }> = [
  {
    title: "Workmanship warranty",
    body: "Every Durafoam installation and repair comes with a written workmanship warranty. Foam roof recoating always carries a 5-year workmanship warranty. New foam roof installations come with a choice of a 5-year or 10-year warranty — the 10-year option requires our upgraded 10-year coating system. Either install warranty can be renewed for another 5 years every time the roof is recoated on schedule, indefinitely. Other scopes: tile R&R (5–10 years on labor), shingle roof (5–10 years on labor), repairs (1–2 years depending on scope). All warranties are in writing — we do not do verbal warranties.",
  },
  {
    title: "Manufacturer warranties",
    body: "In addition to our workmanship warranty, the materials we use carry manufacturer warranties: elastomeric coating systems carry manufacturer material warranties that vary by product, synthetic underlayment for tile R&R (25–50 year material warranty), and architectural shingles (30+ year manufacturer limited warranty). We provide warranty documentation for all materials on request.",
  },
  {
    title: "What our warranty covers",
    body: "Defects in workmanship — if something we installed leaks or fails because of how it was installed, we fix it at no charge during the warranty period. We back up every job.",
  },
  {
    title: "What is not covered",
    body: "Damage from acts of nature beyond normal Phoenix weather (hail, falling debris), damage caused by other contractors working on the roof after our job, normal wear from UV degradation past the expected maintenance interval, and damage from foot traffic or equipment placed on the roof by the homeowner or other trades.",
  },
  {
    title: "How to make a warranty claim",
    body: "Call 480-941-5373 or email quality@durafoaminc.com. Describe the issue. We will schedule a time to inspect and assess. If it is covered under warranty, we fix it. No runaround.",
  },
];

function WarrantyPage() {
  return (
    <>
      <section className="bg-primary py-16 text-white lg:py-20">
        <Container className="max-w-3xl text-center">
          <span className="mx-auto grid h-12 w-12 place-items-center rounded-full bg-white/10">
            <ShieldCheck className="h-6 w-6 text-accent" aria-hidden="true" />
          </span>
          <h1 className="mt-4 text-3xl font-bold sm:text-4xl lg:text-5xl">
            Our roofing warranty
          </h1>
          <p className="mt-4 text-white/80">
            Every Durafoam job comes with a written workmanship warranty plus manufacturer
            material warranties. Here's how it works.
          </p>
        </Container>
      </section>

      <section className="bg-white py-14">
        <Container className="max-w-3xl space-y-10">
          {sections.map((s) => (
            <div key={s.title}>
              <h2 className="text-2xl font-bold text-primary">{s.title}</h2>
              <p className="mt-3 text-[17px] leading-relaxed text-foreground/85">{s.body}</p>
            </div>
          ))}

          <div className="rounded-xl border border-border bg-[var(--surface)] p-6">
            <p className="text-[15px] text-foreground/85">
              Every job comes with a written warranty — ask your estimator for the specific terms
              on your project.
            </p>
            <div className="mt-5 flex flex-wrap gap-3">
              <ButtonLink href="/free-roof-inspection-phoenix" variant="primary" size="lg">
                Schedule free inspection
              </ButtonLink>
              <ButtonLink href="tel:+14809415373" variant="ghost" size="lg">
                <Phone className="h-4 w-4" aria-hidden="true" />
                Call 480-941-5373
              </ButtonLink>
            </div>
          </div>
        </Container>
      </section>
    </>
  );
}
